import React from 'react'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody} from '../../_metronic/helpers'
import {useAuth} from '../modules/auth'
import {MODULES, MODULE_LABELS, PERMISSION_LABELS} from '../modules/supplier-users/users-list/core/_models'
import {PermissionLevel} from '../modules/auth/core/_models'

const PermissionOverviewPage: React.FC = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  const userPermissions = currentUser?.permissions || {}
  const userRole = currentUser?.role

  // Get modules the user has access to
  const accessibleModules = Object.entries(userPermissions)
    .filter(([module, permission]) => permission !== 'NONE')
    .map(([module, permission]) => ({
      module,
      permission: permission as PermissionLevel,
      label: MODULE_LABELS[module as keyof typeof MODULE_LABELS] || module,
      route: getModuleRoute(module)
    }))


  function getModuleRoute(module: string): string {
    const routeMap: Record<string, string> = {
      dashboard: '/dashboard',
      products: '/products/list',
      orders: '/orders',
      discounts: '/discount/add-using-bulk',
      payment_reports: '/payment/transaction-report',
      daily_deals: '/daily-deal/list',
      sales_reports: '/sales/sales-report',
      requests: '/request-add-products',
      user_management: '/supplier-users',
      profile: '/myprofile'
    }
    return routeMap[module] || '/dashboard'
  }

  const getPermissionColor = (permission: PermissionLevel) => {
    switch (permission) {
      case 'EDIT':
        return 'success'
      case 'NONE':
        return 'danger'
      default:
        return 'secondary'
    }
  }

  const getPermissionIcon = (permission: PermissionLevel) => {
    switch (permission) {
      case 'EDIT':
        return 'ki-duotone ki-pencil fs-2'
      case 'NONE':
        return 'ki-duotone ki-cross-circle fs-2'
      default:
        return 'ki-duotone ki-question fs-2'
    }
  }

  const handleNavigateToModule = (route: string) => {
    navigate(route)
  }

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <KTCard>
              <KTCardBody className="py-10">
                <div className="text-center mb-10">
                  {/* Icon */}
                  <div className="mb-5">
                    <i className="ki-duotone ki-shield-tick fs-3x text-success">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                      <span className="path4"></span>
                    </i>
                  </div>

                  {/* Title */}
                  <h1 className="fw-bold text-gray-900 mb-3">
                    {intl.formatMessage({id: 'PERMISSION_OVERVIEW.TITLE'})}
                  </h1>

                  {/* Subtitle */}
                  <p className="fs-5 text-gray-600 mb-8">
                    {intl.formatMessage({id: 'PERMISSION_OVERVIEW.SUBTITLE'})}
                  </p>
                </div>

                {/* User Info */}
                <div className="row justify-content-center mb-10">
                  <div className="col-md-8">
                    <div className="card bg-light-info">
                      <div className="card-body text-center">
                        <h4 className="fw-bold text-gray-800 mb-2">
                          {intl.formatMessage({id: 'PERMISSION_OVERVIEW.WELCOME'})}
                        </h4>
                        <p className="text-gray-600 mb-0">
                          <strong>{currentUser?.name}</strong> ({userRole === 'admin' ? 'Admin' : 'User'})
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Accessible Modules */}
                {accessibleModules.length > 0 && (
                  <div className="mb-10">
                    <h3 className="fw-bold text-gray-900 mb-6 text-center">
                      {intl.formatMessage({id: 'PERMISSION_OVERVIEW.ACCESSIBLE_MODULES'})}
                    </h3>
                    <div className="row g-4">
                      {accessibleModules.map((module) => (
                        <div key={module.module} className="col-md-6 col-lg-4">
                          <div 
                            className="card h-100 cursor-pointer hover-elevate-up"
                            onClick={() => handleNavigateToModule(module.route)}
                            style={{cursor: 'pointer'}}
                          >
                            <div className="card-body text-center p-6">
                              <div className={`mb-4 text-${getPermissionColor(module.permission)}`}>
                                <i className={getPermissionIcon(module.permission)}></i>
                              </div>
                              <h5 className="fw-bold text-gray-900 mb-2">
                                {module.label}
                              </h5>
                              <span className={`badge badge-light-${getPermissionColor(module.permission)}`}>
                                {PERMISSION_LABELS[module.permission]}
                              </span>
                              <div className="mt-3">
                                <button 
                                  className="btn btn-sm btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleNavigateToModule(module.route)
                                  }}
                                >
                                  {intl.formatMessage({id: 'PERMISSION_OVERVIEW.ACCESS'})}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Access Message */}
                {accessibleModules.length === 0 && (
                  <div className="text-center">
                    <div className="mb-5">
                      <i className="ki-duotone ki-information fs-3x text-warning">
                        <span className="path1"></span>
                        <span className="path2"></span>
                        <span className="path3"></span>
                      </i>
                    </div>
                    <h3 className="fw-bold text-gray-900 mb-4">
                      {intl.formatMessage({id: 'PERMISSION_OVERVIEW.NO_ACCESS_TITLE'})}
                    </h3>
                    <p className="fs-5 text-gray-600">
                      {intl.formatMessage({id: 'PERMISSION_OVERVIEW.NO_ACCESS_MESSAGE'})}
                    </p>
                    {/*
                      There was a "My Profile" button here and it could never work. This branch
                      renders only when accessibleModules is empty, and that list is built from
                      EVERY permission including `profile` — so reaching this screen already means
                      profile is NONE, and /myprofile is wrapped in
                      <ProtectedRoute module='profile' requiredLevel='EDIT'>. Clicking it sent the
                      user straight back here. It was the one control on the page, so a locked-out
                      user's only available action did nothing at all.

                      The page is honest without it: contacting an administrator really is the only
                      way forward. Note "My Profile" is the SUPPLIER COMPANY profile (it reads and
                      writes /partner-portal/supplier — bank details, contract, delivery zones), not
                      the user's own account, so the EDIT gate on it is right and must stay.
                    */}
                  </div>
                )}

              </KTCardBody>
            </KTCard>
          </div>
        </div>
      </div>
    </div>
  )
}

export {PermissionOverviewPage}
